// Pure utility functions for subscription business logic

const options = { year: 'numeric', month: 'long', day: 'numeric' };

/**
 * Check if a subscription is cancelled
 * @param {Object} subscription - The subscription object
 * @returns {boolean} Whether the subscription is cancelled
 */
export function isCancelled(subscription) {
  if (!subscription) return true;
  if (subscription.status === 'canceled') return true;
  return false;
}

/**
 * Check if a subscription is a free account
 * @param {Object} subscription - The subscription object (raw or formatted)
 * @returns {boolean} Whether the subscription is free
 */
export function isFreeAccount(subscription) {
  if (!subscription) return false;
  
  // If it's a formatted subscription with total already calculated
  if (subscription.total !== undefined) {
    return subscription.total === 0;
  }
  
  // Otherwise calculate from plan and quantity
  const total = calculateSubscriptionTotal(subscription.plan, subscription.quantity);
  return total === 0;
}

/**
 * Check if a subscription is past due
 * @param {Object} subscription - The subscription object
 * @returns {boolean} Whether the subscription is past due
 */
export function isPastDue(subscription) {
  if (!subscription) return false;
  if (subscription.status === 'past_due') return true;
  return false;
}

/**
 * Check if the current user is the owner of the subscription
 * @param {Object} subscription - The subscription object
 * @param {string} currentUserId - The current user's ID
 * @returns {boolean} Whether the current user is the owner
 */
export function isOwner(subscription, currentUserId) {
  if (!subscription || !currentUserId || !subscription.user_id) {
    return false;
  }
  return currentUserId.toString() === subscription.user_id.toString();
}

/**
 * Format subscription end date
 * @param {string} dateString - The date string to format
 * @returns {string} Formatted date string
 */
export function formatSubscriptionEndDate(dateString) {
  if (!dateString) return '';
  const date = new Date(Date.parse(dateString));
  return date.toLocaleDateString('en-US', options);
}

/**
 * Format subscription price for display
 * @param {Object} subscription - The subscription object
 * @returns {string} Formatted price string (e.g. "$50")
 */
export function formatSubscriptionPrice(subscription) {
  if (!subscription) return '';
  const total = calculateSubscriptionTotal(subscription.plan, subscription.quantity);
  // Show whole numbers without decimals, otherwise show 2 decimal places
  return `$${total % 1 === 0 ? total.toFixed(0) : total.toFixed(2)}`;
}

/**
 * Calculate subscription total
 * @param {Object} plan - The plan object
 * @param {number} quantity - The quantity of seats
 * @returns {number} The total amount
 */
export function calculateSubscriptionTotal(plan, quantity) {
  if (!plan || !plan.amount || !quantity) return 0;
  // Handle amount as string or number
  const amount = typeof plan.amount === 'string' ? parseFloat(plan.amount) : plan.amount;
  return (amount / 100) * quantity;
}

/**
 * Get subscription amount per person formatted for display
 * @param {Object} subscription - The subscription object
 * @returns {string} The formatted amount per person (e.g. "50" or "49.99")
 */
export function getSubscriptionAmount(subscription) {
  if (!subscription?.plan?.amount) return '0';
  
  // Handle amount as string or number
  const amount = typeof subscription.plan.amount === 'string' 
    ? parseFloat(subscription.plan.amount) 
    : subscription.plan.amount;
  
  const amountInDollars = amount / 100;
  
  // Round to nearest integer if no decimals, otherwise show 2 decimal places
  return amountInDollars % 1 === 0 
    ? amountInDollars.toFixed(0)
    : amountInDollars.toFixed(2);
}

/**
 * Get subscription interval (month/year)
 * @param {Object} subscription - The subscription object
 * @returns {string} The interval or 'month' as default
 */
export function getSubscriptionInterval(subscription) {
  return subscription?.plan?.interval || 'month';
}

/**
 * Check if subscription is monthly
 * @param {Object} subscription - The subscription object
 * @returns {boolean} Whether the subscription is monthly
 */
export function isMonthly(subscription) {
  return getSubscriptionInterval(subscription) === 'month';
}

/**
 * Check if subscription is annual
 * @param {Object} subscription - The subscription object
 * @returns {boolean} Whether the subscription is annual
 */
export function isAnnual(subscription) {
  return getSubscriptionInterval(subscription) === 'year';
}

/**
 * Check if subscription is separately invoiced
 * @param {Object} subscription - The subscription object
 * @returns {boolean} Whether the subscription is separately invoiced
 */
export function isSeparatelyInvoiced(subscription) {
  if (!subscription || !subscription.plan?.id) return false;
  return subscription.plan.id.toString() === process.env['SEPARATELY_INVOICED_PLAN_ID'];
}


/**
 * Get user initials from name or email
 * @param {Object} user - The user object
 * @returns {string} User initials
 */
export function getUserInitials(user) {
  if (!user) return '';
  
  if (user.initials) return user.initials;
  
  if (user.name && user.name.length > 0) {
    const nameArray = user.name.split(' ');
    const firstLetter = nameArray[0] ? nameArray[0][0] : "";
    const secondLetter = nameArray[1] ? nameArray[1][0] : "";
    return firstLetter + secondLetter;
  }
  
  if (user.email) {
    return user.email[0].toUpperCase();
  }
  
  return '';
}

/**
 * Format plan amount from API response (converts cents to dollars)
 * @param {string|number} amount - The amount from the plan API (in cents)
 * @returns {number} The amount in dollars
 */
export function formatPlanAmount(amount) {
  if (!amount) return 0;
  // Handle amount as string or number
  const amountValue = typeof amount === 'string' ? parseFloat(amount) : amount;
  return amountValue / 100;
}

/**
 * Format plan price for display
 * @param {Object} plan - The plan object from API
 * @returns {string} Formatted price string (e.g. "$50")
 */
export function formatPlanPrice(plan) {
  if (!plan || !plan.amount) return '$0';
  const amountInDollars = formatPlanAmount(plan.amount);
  // Show whole numbers without decimals, otherwise show 2 decimal places
  return `$${amountInDollars % 1 === 0 ? amountInDollars.toFixed(0) : amountInDollars.toFixed(2)}`;
}

/**
 * Get plan amount formatted for display (without $ sign)
 * @param {Object} plan - The plan object from API
 * @returns {string} The formatted amount (e.g. "50" or "49.99")
 */
export function getPlanAmount(plan) {
  if (!plan || !plan.amount) return '0';
  const amountInDollars = formatPlanAmount(plan.amount);
  // Show whole numbers without decimals, otherwise show 2 decimal places
  return amountInDollars % 1 === 0 ? amountInDollars.toFixed(0) : amountInDollars.toFixed(2);
}

/**
 * Calculate the monthly equivalent price for an annual plan
 * @param {Object} yearlyPlan - The yearly plan object
 * @returns {number} Monthly equivalent price (formatted with appropriate decimals)
 */
export function calculateMonthlyEquivalent(yearlyPlan) {
  if (!yearlyPlan || !yearlyPlan.amount) return 0;
  const monthlyEquivalent = formatPlanAmount(yearlyPlan.amount) / 12;
  
  // Round to 2 decimals to check if it's a whole number
  const rounded = Math.round(monthlyEquivalent * 100) / 100;
  
  // Return as integer if no decimals, otherwise with 2 decimal places
  return rounded % 1 === 0 ? Math.round(rounded) : rounded;
}

/**
 * Calculate the discount percentage between monthly and yearly plans
 * @param {Object} monthlyPlan - The monthly plan object
 * @param {Object} yearlyPlan - The yearly plan object
 * @returns {string|null} Formatted discount percentage (e.g. "28%")
 */
export function calculateAnnualDiscount(monthlyPlan, yearlyPlan) {
  if (!monthlyPlan || !yearlyPlan) return null;
  
  const monthlyAmount = formatPlanAmount(monthlyPlan.amount);
  const yearlyAmount = formatPlanAmount(yearlyPlan.amount);
  const monthlyAnnualized = monthlyAmount * 12;
  
  if (monthlyAnnualized === 0) return null;
  
  const discountPercent = Math.round((1 - (yearlyAmount / monthlyAnnualized)) * 100);
  return `${discountPercent}%`;
}

/**
 * Get the display price based on plan type (monthly shows monthly, yearly shows monthly equivalent)
 * @param {Object} plan - The current plan object
 * @param {boolean} isYearly - Whether viewing yearly plan
 * @returns {number|null} The price to display
 */
export function getDisplayPrice(plan, isYearly) {
  if (!plan || !plan.amount) return null;
  
  if (isYearly) {
    // For yearly plans, show the monthly equivalent
    return calculateMonthlyEquivalent(plan);
  } else {
    // For monthly plans, show the regular amount
    return formatPlanAmount(plan.amount);
  }
}

/**
 * Get pricing data for subscription display
 * @param {Object} plansData - The plans data from API
 * @param {boolean} isYearly - Whether viewing yearly plan
 * @returns {Object} Pricing data with displayPrice, annualAmount, and discount
 */
export function getSubscriptionPricing(plansData, isYearly) {
  if (!plansData?.plans) {
    return {
      displayPrice: null,
      annualAmount: null,
      discount: null
    };
  }

  const monthlyPlan = plansData.plans.find(p => p.interval === 'month');
  const yearlyPlan = plansData.plans.find(p => p.interval === 'year');
  const currentPlan = isYearly ? yearlyPlan : monthlyPlan;

  return {
    displayPrice: getDisplayPrice(currentPlan, isYearly),
    annualAmount: isYearly && yearlyPlan ? formatPlanAmount(yearlyPlan.amount) : null,
    discount: calculateAnnualDiscount(monthlyPlan, yearlyPlan)
  };
}